import { ArrowDownLeft, FileClock, FileSearch, FileX2, Receipt } from 'lucide-react';
import { useCallback } from 'react';

import { useUsuario } from '@/lib/auth/contexto';
import { podeEmAlguma } from '@/lib/auth/permissoes';
import { useEscopoEmpresas } from '@/lib/escopo';
import { useEstadoTabela } from '@/lib/estado-tabela';
import { formatarMoeda } from '@/lib/formato';
import { comTransicao } from '@/lib/movimento';
import { useAtualizarUrl } from '@/lib/url';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { Kpi } from '@/ui/components/kpi';
import { TabelaPaginada } from '@/ui/components/tabela-paginada';
import { CabecalhoPagina } from '@/ui/patterns/cabecalho-pagina';
import { PaginaLista } from '@/ui/patterns/pagina-lista';
import { type ResumoDocumentos } from '@fiscoai/contracts';

import { useDocumentos, useResumoDocumentos } from './api';
import { BaixarLote } from './baixar-lote';
import { BarraFiltrosDocumentos } from './barra-filtros-documentos';
import { colunasDocumentos } from './colunas';
import { useFiltrosDocumentos } from './filtros';
import { GavetaDocumento } from './gaveta-documento';

function Indicadores({ resumo }: { resumo: ResumoDocumentos | undefined }) {
  return (
    <>
      <Kpi
        rotulo="Notas no filtro"
        valor={resumo?.quantidade ?? null}
        icone={<Receipt aria-hidden />}
        destaque
      />
      <Kpi rotulo="Valor total" valor={resumo ? formatarMoeda(resumo.valorTotal) : null} />
      <Kpi
        rotulo="Entradas"
        valor={resumo ? resumo.porDirecao.ENTRADA : null}
        icone={<ArrowDownLeft aria-hidden />}
      />
      <Kpi
        rotulo="Canceladas"
        valor={resumo ? resumo.porSituacao.CANCELADA : null}
        icone={<FileX2 aria-hidden />}
        detalhe={
          resumo && resumo.chavesPendentes > 0
            ? `${String(resumo.chavesPendentes)} chaves aguardam o XML`
            : undefined
        }
      />
    </>
  );
}

/** Colunas ordenáveis no servidor; a lista abre na emissão mais recente. */
const TABELA = {
  chave: 'documentos',
  campos: ['emitidaEm', 'valorTotal', 'importadoEm'] as const,
  padrao: { ordenarPor: 'emitidaEm', ordem: 'desc' },
} as const;

export function PaginaDocumentos() {
  const usuario = useUsuario();
  const escopo = useEscopoEmpresas();
  const { filtros, definir, limpar } = useFiltrosDocumentos();
  const { params, atualizar } = useAtualizarUrl();
  const aberto = params.get('doc');
  const tabela = useEstadoTabela(TABELA);
  const lista = useDocumentos(escopo, filtros, {
    pagina: tabela.pagina,
    porPagina: tabela.porPagina,
    ordenarPor: tabela.ordenarPor,
    ordem: tabela.ordem,
  });
  const resumo = useResumoDocumentos(escopo, filtros);
  const temFiltro = Object.keys(filtros).length > 0;

  const abrir = useCallback(
    (id: string | null, origem?: HTMLElement) => {
      if (origem) origem.style.viewTransitionName = 'detalhe';
      comTransicao(() => {
        if (origem) origem.style.viewTransitionName = '';
        atualizar((proximo) => {
          if (id) proximo.set('doc', id);
          else proximo.delete('doc');
        });
      });
    },
    [atualizar],
  );

  return (
    <PaginaLista
      cabecalho={
        <CabecalhoPagina
          titulo="Documentos fiscais"
          descricao="Todas as notas das empresas no escopo, de entrada e de saída."
          acoes={
            podeEmAlguma(usuario, 'documentos.baixar') && (
              <BaixarLote
                empresaIds={escopo}
                filtros={filtros}
                quantidade={resumo.data?.quantidade}
              />
            )
          }
        />
      }
      indicadores={<Indicadores resumo={resumo.data} />}
      filtros={<BarraFiltrosDocumentos filtros={filtros} definir={definir} limpar={limpar} />}
      lista={
        <TabelaPaginada
          rotulo="Documentos fiscais"
          className="flex-1"
          colunas={colunasDocumentos}
          dados={lista.data?.items ?? []}
          total={lista.data?.total ?? 0}
          pagina={tabela.pagina}
          porPagina={tabela.porPagina}
          aoMudarPagina={tabela.irParaPagina}
          aoMudarPorPagina={tabela.mudarPorPagina}
          ordenacao={tabela.ordenacao}
          aoOrdenar={tabela.ordenar}
          idLinha={(d) => d.id}
          carregando={lista.isPending}
          atualizando={lista.isPlaceholderData}
          selecionadaId={aberto}
          aoAbrir={(d, el) => {
            abrir(d.id, el);
          }}
          vazio={
            lista.isError ? (
              <EstadoVazio
                tom="perigo"
                icone={<FileClock />}
                titulo="A lista não carregou"
                texto="O servidor não respondeu. Confira a conexão e recarregue a página."
              />
            ) : temFiltro ? (
              <EstadoVazio
                icone={<FileSearch />}
                titulo="Nenhuma nota com estes filtros"
                texto="Tire um filtro ou amplie o período para ver mais notas."
              />
            ) : (
              <EstadoVazio
                icone={<Receipt />}
                titulo="Ainda não há notas aqui"
                texto="Importe os XMLs ou o relatório de notas destinadas da SEFAZ para começar."
              />
            )
          }
        />
      }
      detalhe={
        <GavetaDocumento
          id={aberto}
          aoFechar={() => {
            abrir(null);
          }}
          podeBaixar={podeEmAlguma(usuario, 'documentos.baixar')}
        />
      }
    />
  );
}
